import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { LocaleService } from '@next/core-lib/i18n';
import { NavigationActions, ParentNavigationItem } from '@next/core-lib/navigation';
import { WrapperSiteSidenavComponent } from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { Subscription, combineLatest, map, skipWhile, startWith } from 'rxjs';

import { InstallationStatusType } from './settings/models/third-parties.model';
import * as SettingsSelectors from './settings/store/selectors/settings.selectors';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [WrapperSiteSidenavComponent, RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription = Subscription.EMPTY;
  private readonly hasNedapAuthorization$ = this.store
    .select(SettingsSelectors.getNedapInstallations)
    .pipe(
      skipWhile((installations) => !installations),
      map((installations) =>
        installations.some((i) => i.status === InstallationStatusType.authenticated),
      ),
      startWith(false),
    );

  constructor(
    private readonly store: Store,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit(): void {
    this.subscription = combineLatest([
      this.store.select(AuthorizationSelectors.hasAtLeastAdministratorRole),
      this.hasNedapAuthorization$,
      this.localeService.locale$,
    ]).subscribe(([isAtleastAdmin, hasNedapAuthorization]) => {
      const menuItems: ParentNavigationItem[] = [
        {
          relativeUrl: '/feed-advice',
          label: this.localeService.translate('feed-advice-title'),
          icon: 'view_list',
        },
        {
          relativeUrl: '/animal-group',
          label: this.localeService.translate('animal-group-title'),
          icon: 'table_view',
        },
        {
          relativeUrl: '/delivery',
          label: this.localeService.translate('delivery-title'),
          icon: 'local_shipping',
        },
        {
          relativeUrl: '/customer-product',
          label: this.localeService.translate('customer-product-title'),
          icon: 'storefront',
        },
        {
          relativeUrl: '/feed-profit',
          label: this.localeService.translate('feed-profit-title'),
          icon: 'calculate',
        },
        {
          relativeUrl: '/',
          label: this.localeService.translate('Scoring'),
          icon: 'view_list',
          children: [
            {
              relativeUrl: '/fecal-scoring',
              label: this.localeService.translate('fecal-scoring-title'),
              icon: 'view_list',
            },
            {
              relativeUrl: '/tail-biting',
              label: this.localeService.translate('tail-biting-title'),
              icon: 'view_list',
            },
          ],
        },
      ];

      if (isAtleastAdmin) {
        menuItems.push({
          relativeUrl: '/',
          label: this.localeService.translate('management'),
          icon: 'settings',
          children: [
            {
              relativeUrl: '/product',
              label: this.localeService.translate('product-title'),
              icon: 'storefront',
            },
            {
              relativeUrl: '/reference-feed-advice',
              label: this.localeService.translate('reference-feed-advice-title'),
              icon: 'backup_table',
            },
          ],
        });
      }
      menuItems.push(
        {
          relativeUrl: '/settings',
          label: this.localeService.translate('settings.title'),
          icon: 'settings',
        },
        this.getThirdPartyNavigationItem(hasNedapAuthorization),
      );

      this.store.dispatch(NavigationActions.setNavigationItems({ navigationItems: menuItems }));
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getThirdPartyNavigationItem(hasNedapAuthorization: boolean): ParentNavigationItem {
    const children = [
      {
        relativeUrl: '/third-parties',
        label: this.localeService.translate('settings.third-parties.authorizations-title'),
      },
    ];

    if (hasNedapAuthorization) {
      children.push({
        relativeUrl: '/nedap-dashboard',
        label: this.localeService.translate('settings.third-parties.nedap-dashboard-title'),
      });
    }

    return {
      relativeUrl: '/',
      label: this.localeService.translate('settings.third-parties.title'),
      icon: 'settings',
      children,
    };
  }
}
